/**
=========================================================
* Soft UI Dashboard PRO React - v3.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo, useCallback } from "react";

// react-router components
import { Route, Switch, useLocation, useHistory } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Soft UI Dashboard PRO React example components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Soft UI Dashboard PRO React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Soft UI Dashboard PRO React routes
import routes, { mode } from "routes";

// Soft UI Dashboard PRO React contexts
import {
  useSoftUIController,
  setMiniSidenav,
  setLang,
  // setOpenConfigurator,
} from "context/theme";

import { useAuthController, AuthControllerProvider } from "context/auth";
import { Auth } from "api";

// Images
import brand from "assets/images/logo-bf.jpg";

import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";

function App() {
  const [AuthController] = useAuthController();
  const { isAuthenticated } = AuthController;
  const history = useHistory();
  const location = useLocation();

  const [softUiController, softUiDispatch] = useSoftUIController();
  const {
    miniSidenav,
    direction,
    layout,
    //  openConfigurator,
    sidenavColor,
  } = softUiController;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const [position, setPosition] = useState(null);
  const [department, setDepartment] = useState(null);
  const { pathname } = useLocation();
  const accessRoutes = useCallback(
    routes.filter((route) => {
      const access = mode(route, position, department);
      if (access === undefined) {
        return true;
      }
      return access;
    }),
    [position, department]
  );

  useEffect(async () => {
    if (isAuthenticated) {
      try {
        const me = await Auth.me();
        if (me) {
          const { data } = me;
          setPosition(String(data.position).toLowerCase());
          setDepartment(String(data.department).toLowerCase());
          setLang(softUiDispatch, String(data.language).toLowerCase());
        }
      } catch (error) {
        console.error(error);
      }
    } else {
      setPosition(null);
      setDepartment(null);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      history.push("/sign-in");
    }
  }, [isAuthenticated, location.pathname]);

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(softUiDispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(softUiDispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  // const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return (
          <Route
            exact
            path={route.route}
            key={route.key}
            render={() => <route.component mode={mode(route, position, department)} />}
          />
        );
      }

      return null;
    });

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={themeRTL}>
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={brand}
              brandName="Sino Sound"
              routes={accessRoutes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Switch>{getRoutes(accessRoutes)}</Switch>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={brand}
            brandName="Sino Sound"
            routes={accessRoutes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
        </>
      )}
      {layout === "vr" && <Configurator />}
      <Switch>{getRoutes(accessRoutes)}</Switch>
    </ThemeProvider>
  );
}

export default function AuthWrapper() {
  return (
    <I18nextProvider i18n={i18n} defaultNS="translation">
      <AuthControllerProvider>
        <App />
      </AuthControllerProvider>
    </I18nextProvider>
  );
}
